/* eslint-disable react-hooks/exhaustive-deps */
import { Icon, Icons } from 'components/Icon';
import { InfoModal } from 'components/Modal';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCreateStock } from 'common/queries-and-mutations/stock-share';
import { StockShareType } from 'apps/user/types/on-boarding.type';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import TextField from 'components/Form/TextField';
import CustomSelect from 'components/Form/CustomSelect';
import { countriesCurrency } from 'constants/countries-currency';
import Button from 'components/Form/Button';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

export default function AddStockModal({ onClose, onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isError, isLoading } = useCreateStock();

  const formik = useFormik({
    initialValues: {
      stocks: [
        {
          stockId: null,
          companyName: '',
          certificateNo: '',
          registerName: '',
          registerAddress: '',
          country: ''
        }
      ]
    },
    validationSchema: Yup.object({
      stocks: Yup.array().of(
        Yup.object().shape({
          companyName: Yup.string().required('Tell us the name of the company'),
          certificateNo: Yup.string().required(
            'C.S.C.N / Cerification No. is required'
          ),
          registerName: Yup.string()
            .required(`Registrar's name is required`)
            .nullable(),
          registerAddress: Yup.string()
            .required(`Registrar's address is required`)
            .nullable(),
          country: Yup.string().required('Please select country').nullable()
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onStockSubmit(values);
    }
  });

  const onStockSubmit = (formValue: StockShareType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(<Alert message="Stock/Share Successfully Added" type="success" />);
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <div>
      <InfoModal width="w-[60%]" className="h-fit rounded-md">
        <div className="flex flex-row items-center justify-between border-b p-3 pl-6">
          <p className="text-2xl font-bold text-[#353535]">Add Stock</p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="p-8"
          onSubmit={formik.handleSubmit}
        >
          {formik.values.stocks.map((stock, index) => (
            <div key={index} className="my-8">
              <div className="grid md:grid-cols-2 md:gap-9">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`stocks[${index}].companyName`}
                    placeholder=""
                    value={stock.companyName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Name of Company"
                    error={
                      formik.touched.stocks
                        ? formik.errors.stocks?.[index] &&
                          formik.errors.stocks.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.stocks[index])
                            ).companyName
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`stocks[${index}].certificateNo`}
                    placeholder=""
                    value={stock.certificateNo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="C.S.C.S No./Cerificate No."
                    error={
                      formik.touched.stocks
                        ? formik.errors.stocks?.[index] &&
                          formik.errors.stocks.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.stocks[index])
                            ).certificateNo
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-9">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`stocks[${index}].registerName`}
                    placeholder=""
                    value={stock.registerName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={`Registrar's Name`}
                    error={
                      formik.touched.stocks
                        ? formik.errors.stocks?.[index] &&
                          formik.errors.stocks.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.stocks[index])
                            ).registerName
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`stocks[${index}].registerAddress`}
                    placeholder=""
                    value={stock.registerAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={`Registrar's Address`}
                    error={
                      formik.touched.stocks
                        ? formik.errors.stocks?.[index] &&
                          formik.errors.stocks.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.stocks[index])
                            ).registerAddress
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-9">
                <div className="group relative z-0 mb-6 w-full">
                  <CustomSelect
                    options={countriesCurrency.map(country => ({
                      value: country.name,
                      label: country.name
                    }))}
                    selectedOption={{
                      value: stock.country,
                      label: stock.country
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(
                        `stocks[${index}].country`,
                        option?.value
                      );
                    }}
                    label="Country"
                    name="country"
                    isRequired
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.stocks
                        ? formik.errors.stocks?.[index] &&
                          formik.errors.stocks.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.stocks[index])
                            ).country
                          : ''
                        : ''
                    }
                  />
                </div>
              </div>
            </div>
          ))}
          <div className="mt-5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="mt-10 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="custom"
              className="mt-5 rounded p-3"
              label="Add new Stock/Share"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </InfoModal>
    </div>
  );
}
